import React, { useState } from "react";
import { Select, Switch, InputNumber, Spin, Button, Input } from "antd";
import {
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { S3FileUploader } from "../../../Components/S3FileUploader";
import { svg } from "../../../Assets/svg";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";

export interface DrawingItem {
  type: "same" | "custom" | string;
  title: string;
  departments: string[];
  document?: string | null;
  file_name?: string | null;
}

export const getFileNameFromUrl = (key: string) => {
  if (!key) return "";
  try {
    const parts = key.split("/");
    const fullName = parts[parts.length - 1];
    if (fullName.includes("_")) {
      return fullName.substring(fullName.indexOf("_") + 1);
    }
    return fullName;
  } catch (e) {
    return key;
  }
};

interface Props {
  values: {
    is_drawing_require?: boolean;
    drawing_qty?: number;
    drawings?: DrawingItem[];
    // Backward compatibility aliases
    is_drawing_required?: boolean;
    drawings_config?: any[];
  };
  setFieldValue: (field: string, value: any) => void;
  departmentList: any[];
  errors?: any;
  touched?: any;
  submitCount?: number;
}

export const DrawingRequirementSection: React.FC<Props> = ({
  values,
  setFieldValue,
  departmentList,
  errors = {},
  touched = {},
  submitCount = 0,
}) => {
  const isRequired = !!(
    values.is_drawing_require ?? values.is_drawing_required
  );
  const rawDrawings = Array.isArray(values.drawings)
    ? values.drawings
    : Array.isArray(values.drawings_config)
    ? values.drawings_config
    : [];

  const drawings: DrawingItem[] = rawDrawings.map((item: any) => ({
    type:
      item?.type === "same" || item?.drawing_type === "same_each_time"
        ? "same"
        : "custom",
    title: item?.title || "",
    departments: Array.isArray(item?.departments)
      ? item.departments.map((d: any) => d?._id || d)
      : Array.isArray(item?.department_ids)
      ? item.department_ids.map((d: any) => d?._id || d)
      : item?.department_id
      ? [item.department_id?._id || item.department_id]
      : [],
    document: item?.document || item?.file_url || null,
    file_name: item?.file_name || null,
  }));

  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<any>(null);

  const syncDrawings = (
    newIsRequired: boolean,
    newDrawings: DrawingItem[],
    newQty?: number,
  ) => {
    const qty = newIsRequired ? newQty ?? newDrawings.length : 0;
    const finalDrawings = newIsRequired ? newDrawings : [];

    setFieldValue("is_drawing_require", newIsRequired);
    setFieldValue("drawing_qty", qty);
    setFieldValue("drawings", finalDrawings);

    // Keep aliases synced for compatibility
    setFieldValue("is_drawing_required", newIsRequired);
    setFieldValue("drawings_config", finalDrawings);
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      const initial =
        drawings.length > 0
          ? drawings
          : [
              {
                type: "custom",
                title: "",
                departments: [],
                document: null,
                file_name: null,
              },
            ];
      syncDrawings(true, initial, initial.length);
    } else {
      syncDrawings(false, [], 0);
    }
  };

  const handleQtyChange = (targetCount: number) => {
    const validCount = Math.max(1, targetCount || 1);
    const current = [...drawings];
    if (validCount > current.length) {
      const additions: DrawingItem[] = Array.from(
        { length: validCount - current.length },
        () => ({
          type: "custom",
          title: "",
          departments: [],
          document: null,
          file_name: null,
        }),
      );
      syncDrawings(true, [...current, ...additions], validCount);
    } else if (validCount < current.length) {
      syncDrawings(true, current.slice(0, validCount), validCount);
    }
  };

  const handleRemoveItem = (index: number) => {
    if (drawings.length <= 1) {
      const resetItem: DrawingItem[] = [
        {
          type: "custom",
          title: "",
          departments: [],
          document: null,
          file_name: null,
        },
      ];
      syncDrawings(true, resetItem, 1);
    } else {
      const updated = drawings.filter((_, i) => i !== index);
      syncDrawings(true, updated, updated.length);
    }
  };

  const handleUpdateDrawing = (
    index: number,
    partial: Partial<DrawingItem>,
  ) => {
    const updated = drawings.map((item, i) => {
      if (i !== index) return item;
      const merged = { ...item, ...partial };
      if (partial.type === "custom") {
        merged.document = null;
        merged.file_name = null;
      }
      return merged;
    });

    if (!updated[index]) {
      updated[index] = {
        type: "custom",
        title: "",
        departments: [],
        document: null,
        file_name: null,
        ...partial,
      };
    }

    syncDrawings(true, updated, updated.length);
  };

  const departmentOptions = (departmentList || []).map((dept: any) => ({
    label: dept.name || dept.department_name || dept.title,
    value: dept._id,
  }));

  const isSubmitted = (submitCount || 0) > 0;

  return (
    <div className="border border-1 rounded-3 p-3 p-md-4 mb-3 mb-md-4 bg-white shadow-sm">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-2">
          <label className="form-label fs-14 fw-medium text-black mb-0">
            Drawing(s) Required?
          </label>
          <Switch checked={isRequired} onChange={handleToggle} />
        </div>

        {isRequired && (
          <div className="d-flex align-items-center gap-2">
            <label className="form-label fs-14 fw-medium text-black mb-0">
              Qty:
            </label>
            <div
              className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput bg-white"
              style={{ width: "8.5rem" }}
            >
              <Button
                type="text"
                icon={<MinusOutlined />}
                className="btn btn-primary addRemoveBtn"
                style={{
                  backgroundColor: "#212529",
                  color: "white",
                }}
                disabled={drawings.length <= 1}
                onClick={() => handleQtyChange(drawings.length - 1)}
              />
              <InputNumber
                min={1}
                onWheel={(e) => e.currentTarget.blur()}
                value={drawings.length || 1}
                className="border-0 text-center w-100 shadow-none qnum"
                controls={false}
                onChange={(val) => handleQtyChange(Number(val) || 1)}
              />
              <Button
                type="text"
                icon={<PlusOutlined />}
                className="btn btn-primary addRemoveBtn"
                style={{
                  backgroundColor: "#212529",
                  color: "white",
                }}
                onClick={() => handleQtyChange(drawings.length + 1)}
              />
            </div>
          </div>
        )}
      </div>

      {isRequired && (
        <div className="mt-3 d-flex flex-column gap-3">
          {drawings.map((item, dIdx) => {
            const isSame = item.type === "same";
            const selectedDepartments = Array.isArray(item.departments)
              ? item.departments
              : [];

            const drawingErrors =
              errors?.drawings?.[dIdx] ||
              errors?.drawings_config?.[dIdx] ||
              {};
            const drawingTouched =
              touched?.drawings?.[dIdx] ||
              touched?.drawings_config?.[dIdx] ||
              {};

            const deptError =
              (drawingTouched?.departments ||
                drawingTouched?.department_ids ||
                isSubmitted) &&
              (drawingErrors?.departments || drawingErrors?.department_ids);
            const titleError =
              (drawingTouched?.title || isSubmitted) && drawingErrors?.title;
            const fileError =
              isSame &&
              (drawingTouched?.document ||
                drawingTouched?.file_url ||
                isSubmitted) &&
              (drawingErrors?.document || drawingErrors?.file_url);

            return (
              <div
                key={dIdx}
                className="border border-1 rounded-3 p-3 bg-light"
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="badge bg-dark text-white fs-13 px-2 py-1">
                    Drawing {dIdx + 1}
                  </span>
                  {drawings.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0 d-flex align-items-center"
                      onClick={() => handleRemoveItem(dIdx)}
                      title="Remove Drawing"
                    >
                      <DeleteOutlined style={{ fontSize: "16px" }} />
                    </button>
                  )}
                </div>

                <div className="row g-3 align-items-start">
                  <div className="col-md-4">
                    <div className="form-group mb-0 custom-select">
                      <label className="form-label fs-14 fw-medium text-black d-block mb-2">
                        Department(s) <span className="text-danger">*</span>
                      </label>
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select Department(s)"
                        value={selectedDepartments}
                        onChange={(vals) =>
                          handleUpdateDrawing(dIdx, { departments: vals })
                        }
                        options={departmentOptions}
                        suffixIcon={svg?.selectDownArrow}
                        className={`w-100 ${
                          deptError ? "is-invalid-select" : ""
                        }`}
                        style={{ minHeight: "48px" }}
                      />
                      {deptError && (
                        <div className="invalid-feedback d-block">
                          {String(deptError)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group mb-0 custom-select">
                      <label className="form-label fs-14 fw-medium text-black d-block mb-2">
                        Type <span className="text-danger">*</span>
                      </label>
                      <Select
                        value={item.type || "custom"}
                        onChange={(val) =>
                          handleUpdateDrawing(dIdx, { type: val })
                        }
                        options={[
                          {
                            label: "Custom Drawing Each Time",
                            value: "custom",
                          },
                          {
                            label: "Same Drawing Each Time",
                            value: "same",
                          },
                        ]}
                        suffixIcon={svg?.selectDownArrow}
                        className="w-100"
                        style={{ minHeight: "48px" }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group mb-0">
                      <label className="form-label fs-14 fw-medium text-black d-block mb-2">
                        Title <span className="text-danger">*</span>
                      </label>
                      <Input
                        className={`form-control form-control-lg fs-14 ${
                          titleError ? "is-invalid" : ""
                        }`}
                        placeholder="Drawing Title (e.g. Splicing Detail)"
                        value={item.title || ""}
                        onChange={(e) =>
                          handleUpdateDrawing(dIdx, { title: e.target.value })
                        }
                        style={{
                          height: "48px",
                          borderRadius: "8px",
                          borderColor: titleError ? "#dc3545" : "#d9d9d9",
                        }}
                      />
                      {titleError && (
                        <div className="invalid-feedback d-block">
                          {String(titleError)}
                        </div>
                      )}
                    </div>
                  </div>

                  {isSame && (
                    <div className="col-12 mt-3">
                      <label className="form-label fs-14 fw-medium text-black mb-2 d-block">
                        Locked Drawing File (Permanent){" "}
                        <span className="text-danger">*</span>
                      </label>
                      {item.document ? (
                        <div className="d-flex align-items-center justify-content-between p-3 bg-white border rounded-3 shadow-sm">
                          <div className="d-flex align-items-center gap-2 text-truncate me-3">
                            <PaperClipOutlined className="text-primary fs-18" />
                            <span
                              className="fw-semibold text-dark text-truncate fs-14"
                              title={getFileNameFromUrl(item.file_name || item.document || "")}
                            >
                              {getFileNameFromUrl(item.file_name || item.document || "") ||
                                "Locked Drawing"}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            <Button
                              type="text"
                              icon={svg?.viewIcon}
                              onClick={() => {
                                setPreviewFile(item.document);
                                setPreviewModalOpen(true);
                              }}
                              title="View Drawing"
                              className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                            />
                            <Button
                              type="text"
                              icon={svg?.deleteBtn}
                              onClick={() => {
                                handleUpdateDrawing(dIdx, {
                                  document: null,
                                  file_name: null,
                                });
                              }}
                              title="Remove File"
                              className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <S3FileUploader
                            folder="part_component_drawings"
                            accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf,.svg"
                            multiple={false}
                            onUploadStart={() => setUploadingIndex(dIdx)}
                            onSuccess={(key, fileName) => {
                              setUploadingIndex(null);
                              handleUpdateDrawing(dIdx, {
                                document: key,
                                file_name: fileName,
                              });
                            }}
                            onError={() => setUploadingIndex(null)}
                            className="bg-white rounded-3 py-3"
                            style={{
                              border: fileError
                                ? "1.5px dashed #dc3545"
                                : "1.5px dashed #bbb",
                            }}
                          >
                            {uploadingIndex === dIdx ? (
                              <div className="py-2 text-center">
                                <Spin tip="Uploading drawing file..." />
                              </div>
                            ) : (
                              <div className="text-center py-2 cursor-pointer">
                                <p className="ant-upload-drag-icon d-flex justify-content-center mb-1">
                                  <span className="svg-32">
                                    {svg?.importIcon}
                                  </span>
                                </p>
                                <p className="ant-upload-text fw-semibold fs-13 mb-0">
                                  Drag & Drop Drawing File /{" "}
                                  <span className="text-primary text-decoration-underline">
                                    CLICK HERE
                                  </span>
                                </p>
                                <small className="text-muted fs-11">
                                  Supports PDF, DWG, DXF, PNG, JPG, SVG
                                </small>
                              </div>
                            )}
                          </S3FileUploader>
                          {fileError && (
                            <div className="invalid-feedback d-block mt-1">
                              {String(fileError)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {previewModalOpen && previewFile && (
        <CommonFilePreviewModal
          open={previewModalOpen}
          file={previewFile}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};
